import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi, DashboardSummaryDto } from '@/services/api';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';

interface UseDashboardDataResult {
  data: DashboardSummaryDto | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function isAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError') ||
    (error instanceof Error && error.message.includes('aborted'))
  );
}

export function useDashboardData(): UseDashboardDataResult {
  const { isLive } = useDataMode();
  const { currentCompany, isLoading: companyLoading } = useCompany();
  const [data, setData] = useState<DashboardSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Aborts the in-flight summary request when the dashboard unmounts (e.g. the
  // user navigates to the Planner) or when a newer fetch supersedes it, so the
  // dashboard-only request never outlives the page that needs it.
  const abortRef = useRef<AbortController | null>(null);
  // Company the currently held summary belongs to, so a company switch can drop
  // it before the replacement arrives instead of rendering another company's data.
  const loadedCompanyIdRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (!isLive('overview')) {
      loadedCompanyIdRef.current = null;
      setData(null);
      setError(null);
      return;
    }

    // Wait until company context has resolved to avoid fetching without a company filter
    if (companyLoading || !currentCompany) {
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    if (loadedCompanyIdRef.current !== currentCompany.id) {
      loadedCompanyIdRef.current = currentCompany.id;
      setData(null);
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dashboardApi.getSummary(currentCompany.id, controller.signal);
      if (controller.signal.aborted) return;
      setData(result);
    } catch (err) {
      if (controller.signal.aborted || isAbort(err)) return;
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(message);
      loadedCompanyIdRef.current = null;
      setData(null);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [isLive, currentCompany, companyLoading]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [fetchData]);

  return { data, isLoading: isLoading || companyLoading, error, refetch: fetchData };
}
